import React, { useEffect, useRef, forwardRef } from 'react';

import { cn } from '../utils/utils';

type HeadingProps = {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  children: React.ReactNode;
  className?: string;
  id?: string;
  visuallyHidden?: boolean;
  autoFocus?: boolean;
};

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level = 1, children, id, visuallyHidden = false, autoFocus = false, className = '' }, ref) => {
    const innerRef = useRef<HTMLHeadingElement>(null);

    const combinedRef = (node: HTMLHeadingElement | null) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
      innerRef.current = node;
    };

    useEffect(() => {
      if (autoFocus && level === 1 && innerRef.current) {
        innerRef.current.focus();
      }
    }, [autoFocus, level]);

    const tagMap = {
      1: 'h1',
      2: 'h2',
      3: 'h3',
      4: 'h4',
      5: 'h5',
      6: 'h6',
    } as const;

    const Tag = tagMap[level] || 'h1';

    const hiddenClass = visuallyHidden ? 'sr-only' : '';

    const sizeClasses = {
      1: 'rm-font-h1-bold',
      2: 'rm-font-h2-bold',
      3: 'rm-font-h3-bold',
      4: 'rm-font-h4-bold',
      5: 'rm-font-h5-bold',
      6: 'rm-font-h6-bold',
    };

    const classes = cn(className, sizeClasses[level], hiddenClass);

    return (
      <Tag id={id} className={classes} tabIndex={level === 1 ? 0 : undefined} ref={combinedRef}>
        {children}
      </Tag>
    );
  }
);

Heading.displayName = 'Heading';
